import {
    EnumButtonName,
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import {
    // eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.palette.gradient
    DS_GRADIENT,
    DS_SHAPE_BORDER_RADIUS,
    DS_WIDTH,
    PRIMITIVES,
} from '@calm/evexia-design-tokens';
import {
    DialogCloseButton,
    DialogCloseButtonProps,
} from '@rhg/components/dialog-close-button';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    SxProps,
    Theme,
    Typography,
    TypographyProps,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { CSSProperties, Fragment, ReactElement, useId } from 'react';
import { DialogProps } from '../overrides/dialog';
import { sxAppend } from '../sx';
import { SimpleDialogTitle } from './title';
import {
    BodyTextAlign,
    DialogButton,
    IconColor,
    RawDialogButton,
    SimpleDialogBackground,
    SimpleDialogCloseReason,
} from './types';

const styleDialogContainer = (hasBottomContent: boolean) => ({
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowY: hasBottomContent ? 'auto' : null,
});
const styleDialogPaper = {
    backgroundColor: 'background.paper',
    backgroundImage: 'none',
};
// Each level below the paper is a flex column with minHeight 0, so the paper's bounded height
// reaches the caller's content, which then decides what scrolls.
const styleBoundPaper = { overflowY: 'hidden' };
const styleBoundColumn = {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
};
const styleBoundContentContainer = { minHeight: 0, overflowY: 'hidden' };
const styleBoundWrapper = { ...styleBoundColumn, width: '100%' };
const styleBottomActions = {
    py: 1,
    display: 'flex',
    justifyContent: 'center',
    px: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
};
const styleBottomActionsLine = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : 'background.primary',
    borderTop: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${theme.palette.divider}`
            : 'none',
};
const styleButtonsContainer = {
    gap: 4,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: { xs: 'column-reverse', sm: 'column-reverse', md: 'row' },
};
const styleCloseButtonContainer = {
    position: 'absolute',
    top: 0,
    p: (theme: Theme) => theme.ds.spacingUnits.SIDE_SMALL,
};

const styleBottomSheetDialogRoot = {
    '& .MuiDialog-container': {
        alignItems: 'flex-end',
    },
};
const styleBottomSheetPaper = {
    m: 0,
    width: '100%',
    maxHeight: 'calc(100% - 32px)',
    borderTopLeftRadius: `${DS_SHAPE_BORDER_RADIUS.CARD}px`,
    borderTopRightRadius: `${DS_SHAPE_BORDER_RADIUS.CARD}px`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
};

const styleCloseButtonContainerFullScreen = {
    p: (theme: Theme) => theme.ds.spacingUnits.SIDE_LARGE,
};

const styleCloseButtonContainerLeft = {
    left: 0,
    right: 'unset',
};
const styleCloseButtonContainerRight = {
    right: 0,
};

export const styleSimpleDialogButton = {
    width: '100%',
    maxWidth: { xs: '100%', sm: '100%', md: DS_WIDTH.FORM_MAX_WIDTH },
    my: 2,
};
export const dialogXPaddingDefault = { xs: 5, sm: 5, md: 6, lg: 8 };
const styleContentContainer = (hasBottomContent: boolean) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: hasBottomContent ? null : 'auto',
    pt: 10,
    px: dialogXPaddingDefault,
});

const styleContentContainerFullScreen = {
    pt: 20,
};

const styleFullGradient = {
    background: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.gradient.backgroundPrimary
            : DS_GRADIENT.LIGHT_ICON_PRIMARY,
};

const styleContentGradient = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

const styleDialogContent = {
    pb: 3.5,
    color: 'text.primary',
    flexGrow: 1,
};
const styleDialogTitle = {
    mb: 4,
};
const styleDialogText = {
    color: 'text.primary',
};
const styleIconContainer = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'background.paper' : 'mask.black.20p',
    color: 'primary.main',
    width: '40px',
    height: '40px',
    p: 1,
    mb: 4,
    '& > svg': {
        width: '32px',
        height: '32px',
    },
};
const styleIconContainerPrimary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.main' : 'primary.dark',
};
const styleIconContainerSecondary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.main' : 'secondary.dark',
};
const styleIconContainerWarning = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark',
    color: 'warning.main',
};
const styleIconContainerInfo = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.light' : 'info.dark',
    color: 'info.main',
};
const styleIconContainerFullScreen = {
    width: { xs: '64px', sm: '64px', md: '96px' },
    height: { xs: '64px', sm: '64px', md: '96px' },
    p: { xs: '6px', sm: '6px', md: '18px' },
    '& > svg': {
        width: { xs: '52px', sm: '52px', md: '60px' },
        height: { xs: '52px', sm: '52px', md: '60px' },
    },
};
const styleNoIconPadding = {
    p: 0,
};
const styleIconContainerWithBackgroundImage = {
    position: 'absolute',
    bottom: '-12%',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
    mb: 0, // Override default margin
};
const styleBackgroundImage = (backgroundImage: string) => ({
    position: 'relative',
    height: '260px',
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    flexShrink: 0, // Prevent the image container from shrinking
});
export const DEFAULT_DIALOG_IMAGE_SIZE = 120;

// React 19: Added index parameter and Fragment wrapper for raw components to ensure unique keys in mapped arrays
function renderButton(
    {
        title,
        isVisible,
        rawComponent,
        ...otherProps
    }: DialogButton | RawDialogButton,
    index: number
) {
    if (!isVisible || isVisible()) {
        if (rawComponent) {
            return <Fragment key={`button-${index}`}>{rawComponent}</Fragment>;
        } else {
            const buttonProps = otherProps as ButtonProps;
            return (
                <Button
                    key={title}
                    color="primary"
                    size="large"
                    variant="contained"
                    {...buttonProps}
                    sx={sxAppend(styleSimpleDialogButton, buttonProps.sx)}
                >
                    {title}
                </Button>
            );
        }
    } else {
        return null;
    }
}

export type SimpleDialogProps = {
    bodyText?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    contentWrapperStyle?: SxProps<Theme>;
    buttons?: (DialogButton | RawDialogButton)[];
    disableBackdropClick?: boolean;
    disableMobileFullscreen?: boolean;
    /** Presents as a sheet docked to the bottom of the viewport (rounded top corners only, backdrop visible above it) on mobile instead of the default fullscreen takeover. No effect above the mobile breakpoint. */
    bottomSheetOnMobile?: boolean;
    /** Bounds `content` to the dialog's height instead of scrolling it, so `content` scrolls its own region and keeps anything above it, the title and the buttons in view. `content` must be a flex column that gives its scrolling region `minHeight: 0` and `overflowY: auto`. */
    boundContentHeight?: boolean;
    hideCloseButton?: boolean;
    /** Announces the dialog's title + bodyText/content via role="status"/aria-live when it renders as a toast-style, non-modal-focused confirmation rather than a dialog the user navigated into. Opt-in, default off — does not change any other SimpleDialog consumer. */
    isLiveRegion?: boolean;
    closeButtonVariant?: DialogCloseButtonProps['variant'];
    isOpen: boolean;
    closeButtonPosition?: 'left' | 'right';
    background?: SimpleDialogBackground;
    noIconPadding?: boolean;
    dialogMaxWidth?: CSSProperties['maxWidth'];
    trackDialog: EnumDialogName;
    trackScreen?: EnumScreenName;
    trackFeature?: EnumFeature;
    trackProps?: EventProperties;
    skipDialogViewedTracking?: boolean;
    onClose: () => void;
    /** Fires alongside onClose with how the dialog was dismissed */
    onDismiss?: (reason: SimpleDialogCloseReason) => void;
    title?: string;
    titleVariant?: TypographyProps['variant'];
    titleTextAlign?: TypographyProps['textAlign'];
    titleColor?: TypographyProps['color'];
    titleWrapperStyle?: SxProps<Theme>;
    titleStyle?: SxProps<Theme>;
    bottomActionsWrapperStyle?: SxProps<Theme>;
    buttonsContainerStyle?: SxProps<Theme>;
    closeButtonWrapperStyle?: SxProps<Theme>;
    contentContainerStyle?: SxProps<Theme>;
    bottomContent?: ReactElement | null;
    trackCloseButton?: EnumButtonName;
    iconContainerOverride?: ReactElement;
    backgroundImage?: string;
    imageSrc?: string;
    imageAlt?: string;
    imageSize?: number;
} & Partial<Omit<DialogProps, 'content'>> &
    (
        | {
              icon?: ReactElement | null;
              inheritIconSize?: boolean;
              iconColor?: IconColor;
              iconContainerStyle?: SxProps<Theme>;
          }
        | {
              icon?: never;
              inheritIconSize?: never;
              iconColor?: never;
              iconContainerStyle?: never;
          }
    );

const styleImageContainer = {
    mb: 4,
};
const styleImage = {
    borderRadius: PRIMITIVES.radius._8,
};

function SimpleDialogIcon({
    inheritIconSize,
    isFullScreen,
    icon,
    iconColor,
    noIconPadding,
    iconContainerStyle,
    iconCustomStyle,
    imageSrc,
    imageAlt,
    imageSize = DEFAULT_DIALOG_IMAGE_SIZE,
}: {
    isFullScreen?: boolean;
    inheritIconSize?: boolean;
    icon?: ReactElement | null;
    iconColor?: IconColor;
    noIconPadding?: boolean;
    iconContainerStyle?: SxProps<Theme>;
    iconCustomStyle?: SxProps<Theme>;
    imageSrc?: string;
    imageAlt?: string;
    imageSize?: number;
}) {
    if (imageSrc) {
        return (
            <Box sx={styleImageContainer}>
                <NextImage
                    width={imageSize}
                    height={imageSize}
                    src={imageSrc}
                    unoptimized
                    alt={imageAlt || ''}
                    style={styleImage}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={sxAppend(
                !inheritIconSize && styleIconContainer,
                !inheritIconSize &&
                    isFullScreen &&
                    styleIconContainerFullScreen,
                iconColor === 'primary' && styleIconContainerPrimary,
                iconColor === 'secondary' && styleIconContainerSecondary,
                iconColor === 'warning' && styleIconContainerWarning,
                iconColor === 'info' && styleIconContainerInfo,
                noIconPadding && styleNoIconPadding,
                iconContainerStyle,
                iconCustomStyle
            )}
        >
            {icon}
        </Box>
    );
}

/**
 * Before using this component directly, see if you can use the `SheetDialog` component instead as it provides styling that aligns more directly with our design system.
 */
export const SimpleDialog = ({
    bodyText,
    bodyTextAlign = 'left',
    content,
    contentWrapperStyle = undefined,
    buttons = [],
    'aria-labelledby': labelledBy,
    'aria-label': ariaLabel,
    isLiveRegion = false,
    icon,
    iconColor = 'transparent',
    hideCloseButton = false,
    disableBackdropClick = false,
    disableMobileFullscreen = false,
    bottomSheetOnMobile = false,
    boundContentHeight = false,
    background = SimpleDialogBackground.None,
    inheritIconSize = false,
    noIconPadding = false,
    dialogMaxWidth = undefined,
    isOpen,
    closeButtonPosition = 'right',
    closeButtonVariant,
    onClose,
    onDismiss,
    title,
    titleVariant,
    titleTextAlign = 'center',
    titleColor,
    titleWrapperStyle = undefined,
    titleStyle,
    trackDialog,
    trackScreen,
    trackFeature,
    trackProps,
    skipDialogViewedTracking,
    trackCloseButton = 'Close',
    bottomActionsWrapperStyle,
    buttonsContainerStyle,
    closeButtonWrapperStyle,
    contentContainerStyle,
    bottomContent,
    iconContainerStyle,
    iconContainerOverride,
    backgroundImage,
    imageSrc,
    imageAlt,
    imageSize,
    ...dialogProps
}: SimpleDialogProps) => {
    const id = useId();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isBottomSheet =
        bottomSheetOnMobile && isMobile && !dialogProps.fullScreen;
    const isFullScreen =
        (!disableMobileFullscreen && isMobile && !isBottomSheet) ||
        dialogProps.fullScreen;

    const showCloseButton = !hideCloseButton;

    const handleOnClose = (
        _: any,
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return;
        }
        onDismiss?.(reason);
        onClose();
    };

    const shouldShowBackgroundImage = backgroundImage && isMobile;
    return (
        <Dialog
            aria-labelledby={labelledBy ?? (title ? id : undefined)}
            {...dialogProps}
            PaperProps={{
                elevation: isFullScreen ? undefined : 1,
                // MUI's <Dialog> spreads a bare `aria-label` prop onto the Modal root
                // rather than the Paper element that carries role="dialog", so it must
                // be forwarded via PaperProps to actually name the dialog. The same
                // override is needed for `aria-labelledby`: MUI's own useId() always
                // generates a fresh id for the Paper when no value is supplied (meant
                // to pair with its own DialogTitle), which is a dangling reference here
                // since we render a custom title instead — explicitly clearing it to
                // our own computed value (including undefined) removes that default.
                'aria-label': ariaLabel,
                'aria-labelledby': labelledBy ?? (title ? id : undefined),
                ...dialogProps.PaperProps,
                sx: sxAppend(
                    {
                        maxWidth: dialogMaxWidth ?? 'none',
                    },
                    styleDialogPaper,
                    isBottomSheet && styleBottomSheetPaper,
                    boundContentHeight && styleBoundPaper,
                    dialogProps.PaperProps?.sx
                ),
            }}
            sx={sxAppend(
                dialogProps.sx,
                isBottomSheet && styleBottomSheetDialogRoot
            )}
            fullScreen={isFullScreen}
            trackDialog={trackDialog}
            trackScreen={trackScreen}
            trackFeature={trackFeature}
            trackProps={trackProps}
            skipDialogViewedTracking={skipDialogViewedTracking}
            onClose={handleOnClose}
            open={isOpen}
        >
            <Box
                sx={sxAppend(
                    isFullScreen && styleDialogContainer(!!bottomContent),
                    boundContentHeight && !isFullScreen && styleBoundColumn,
                    background === SimpleDialogBackground.Full &&
                        styleFullGradient
                )}
            >
                {shouldShowBackgroundImage && (
                    <Box sx={styleBackgroundImage(backgroundImage)}>
                        {(iconContainerOverride || icon || imageSrc) && (
                            <SimpleDialogIcon
                                isFullScreen={isFullScreen}
                                inheritIconSize={inheritIconSize}
                                icon={icon}
                                iconColor={iconColor}
                                noIconPadding={noIconPadding}
                                iconContainerStyle={iconContainerStyle}
                                iconCustomStyle={
                                    styleIconContainerWithBackgroundImage
                                }
                                imageSrc={imageSrc}
                                imageAlt={imageAlt}
                                imageSize={imageSize}
                            />
                        )}
                    </Box>
                )}
                {showCloseButton && (
                    <Box
                        sx={sxAppend(
                            styleCloseButtonContainer,
                            closeButtonPosition === 'left' &&
                                styleCloseButtonContainerLeft,
                            closeButtonPosition === 'right' &&
                                styleCloseButtonContainerRight,
                            isFullScreen && styleCloseButtonContainerFullScreen,
                            closeButtonWrapperStyle
                        )}
                    >
                        <DialogCloseButton
                            variant={closeButtonVariant}
                            onClick={() => {
                                onDismiss?.('closeButton');
                                onClose();
                            }}
                            trackButton={trackCloseButton}
                            trackScreen={trackScreen}
                            trackFeature={trackFeature}
                            trackProps={trackProps}
                            isFullScreen={isFullScreen}
                        />
                    </Box>
                )}
                <Box
                    {...(!bottomContent &&
                        (labelledBy || title) && {
                            tabIndex: 0,
                            role: 'group',
                            'aria-labelledby': labelledBy ?? id,
                        })}
                    sx={sxAppend(
                        styleContentContainer(!!bottomContent),
                        background === SimpleDialogBackground.Content &&
                            styleContentGradient,
                        isFullScreen && styleContentContainerFullScreen,
                        boundContentHeight && styleBoundContentContainer,
                        contentContainerStyle
                    )}
                >
                    {!shouldShowBackgroundImage &&
                        (iconContainerOverride
                            ? iconContainerOverride
                            : (icon || imageSrc) && (
                                  <SimpleDialogIcon
                                      isFullScreen={isFullScreen}
                                      inheritIconSize={inheritIconSize}
                                      icon={icon}
                                      iconColor={iconColor}
                                      noIconPadding={noIconPadding}
                                      iconContainerStyle={iconContainerStyle}
                                      imageSrc={imageSrc}
                                      imageAlt={imageAlt}
                                      imageSize={imageSize}
                                  />
                              ))}
                    <Box
                        role={isLiveRegion ? 'status' : undefined}
                        aria-live={isLiveRegion ? 'polite' : undefined}
                        sx={boundContentHeight ? styleBoundWrapper : undefined}
                    >
                        {title && (
                            <MaxColumnsContainer
                                columns={6}
                                sx={sxAppend(
                                    styleDialogTitle,
                                    titleWrapperStyle
                                )}
                                id={id}
                            >
                                <SimpleDialogTitle
                                    title={title}
                                    titleVariant={titleVariant}
                                    titleTextAlign={titleTextAlign}
                                    titleColor={titleColor}
                                    sx={sxAppend(styleDialogTitle, titleStyle)}
                                />
                            </MaxColumnsContainer>
                        )}
                        {(bodyText || content) && (
                            <MaxColumnsContainer
                                columns={6}
                                sx={sxAppend(
                                    styleDialogContent,
                                    boundContentHeight && styleBoundColumn,
                                    contentWrapperStyle
                                )}
                            >
                                {bodyText &&
                                    bodyText.split('\n').map((line, index) => (
                                        <Typography
                                            key={line}
                                            variant="body1"
                                            component="div"
                                            textAlign={bodyTextAlign}
                                            color="text.body"
                                            sx={{
                                                ...styleDialogText,
                                                mt: index === 0 ? 0 : 2,
                                            }}
                                        >
                                            {line}
                                        </Typography>
                                    ))}
                                {content}
                            </MaxColumnsContainer>
                        )}
                    </Box>
                    {bottomContent}
                </Box>
                {/* Only show the dialog buttons if at least one is visible */}
                {!!buttons.find((x) => !x.isVisible || x.isVisible()) && (
                    <Box
                        sx={sxAppend(
                            styleBottomActions,
                            isFullScreen && styleBottomActionsLine,
                            bottomActionsWrapperStyle
                        )}
                    >
                        <MaxColumnsContainer
                            columns={6}
                            sx={sxAppend(
                                styleButtonsContainer,
                                buttonsContainerStyle
                            )}
                        >
                            {buttons.map((button, index) =>
                                renderButton(button, index)
                            )}
                        </MaxColumnsContainer>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default SimpleDialog;
